import { ICourse2 } from '@et/types/Courses'
import { colors } from '@styles/global/colors'
import { PAYMENT_TYPES_THEME } from '@et/types/Enums'

const slug = 'procreate-for-beginners'
const title = 'Procreate for Beginners'
const course: ICourse2 = {
  theme: PAYMENT_TYPES_THEME.DARK,
  title,
  slug,
  videoId: `f5gs72o0fe`,
  keywords: [
    'Beginner', 'procreate', 'lettering', 'hand lettering', 'design', 'digital art', 'digital', 'ipad', 'ipad lettering', 'typography', 'tutorial', 'tutorials', 'how to', 'diy', 'beginners', 'introduction', 'foundations', 'foundation', 'artwork', 'hand drawn', 'brushes', 'interface', 'project', 'project based learning', 'learn', 'learning', 'course', 'online course', 'guide', 'Procreate 5x', 'intro to procreate', '5x',
  ],
  price: '0',
  salePrice: '0',
  paymentPlan:{
    price: '0',
    perMonth: '3 Payments of $0/m',
    savings: '0'
  },
  motionMail: '',
  onSale: false,
  tagline: 'The ultimate beginner’s course for beautiful Procreate artwork',
  classDescriptor: 'Start creating beautiful iPad artwork today',
  intro: {
    imgName: 'procreate101/tablet-side.png',
    imgAlt: 'Welcome to Procreate for Beginners',
    bullets: [
      {
        title: 'Tools and Interface walk throughs',
        desc:
          'Learn about all the tools you’ll need to be familiar with first. The how’s and the why’s explained, so if you’d like to alter settings later on, you’ll do so with confidence.',
      },
      {
        title: 'Project based learning',
        desc:
          'After each tool walk through module, you’ll complete a project using those exact same tools. This will reinforce where the tools are and how they work while creating beautiful artwork at the same time.',
      },
      {
        title: 'Resolution, color profiles + exporting',
        desc:
          'Get started on the right foot knowing how to choose a color profile, size and resolution for your canvas for the best possible outcome. When it’s complete, you’ll know exactly what to do to hand off your files or export them as needed for any use.',
      },
      {
        title: 'Animation basics',
        desc:
          'Bring your Procreate projects to life by adding animation! Through one mini animation project and the course’s final, large project, you’ll be equipped to add animation to any of your artwork moving forward. We go over all the basics you’ll need to know including how to set up your file, use the animation assist palette and export your animation for its intended purpose.',
      },
    ],
  },
  projects: {
    beginner: [
      {
        title: 'Project 1',
        backgroundColor: `#f8d1b9`,
        textColor: `#765051`,
        subheadColor: `#fff`,
        projectImg: `procreate101/project-1.jpg`,
        projectImgMobile: `procreate101/project-1.jpg`,
        type: 'floral initial',
        desc:
          'In the first project of the course, we create a layered floral initial! Gain lots of practice using the monoweight brush and create different colored elements that you will layer separately, adjust layer order with and create layer groups, too! This foundational skill is crucial for all artwork you make in the future, and after this project, you’ll be a layer master!',
      },
      {
        title: 'Project 2',
        backgroundColor: `#9e9377`,
        textColor: `#f8f8df`,
        subheadColor: `#203536`,
        projectImg: `procreate101/project-2.jpg`,
        projectImgMobile: `procreate101/project-2.jpg`,
        type: 'hello',
        desc:
          'Understanding and utilizing clipping masks and layer masks are the primary goals in this second project! We make use of the skills and tools we’ve already learned while setting typeable text, practicing overlapping and underlapping flourishes with layer masks, and we finish it all off with teardrop elements + dotted line details to balance our composition.',
      },
      {
        title: 'Project 3',
        backgroundColor: `#8b0043`,
        textColor: `#f8f8df`,
        subheadColor: `#fe6a92`,
        projectImg: `procreate101/project-3.jpg`,
        projectImgMobile: `procreate101/project-3.jpg`,
        type: 'hey',
        desc:
          'In the third project of the course, we dive into 3D lettering! I’m sharing my easy method for creating pop-off-the screen lettering with extrusion shading and a fun background texture. We’ll once again make use of clipping masks and layer masks, this time applying a layer mask *to* a clipping mask! We’re getting all sorts of crazy over here!',
      },
      {
        title: 'Project 4',
        backgroundColor: `#ffcac5`,
        textColor: `#1f3646`,
        subheadColor: `#1f3646`,
        projectImg: `procreate101/project-4.jpg`,
        projectImgMobile: `procreate101/project-4.jpg`,
        video: true,
        type: 'draw',
        desc:
          'We finish off the course with a ‘challenge’ project. This is a more advanced project, but we’re using everything you will have already learned when you get here. In this project, we put our new animation skills and layer masking skills to use by creating a ‘drawn in’ lettering animation and export it so it’s ready for social media or the web!',
      },
    ],
    advanced: [],
  },
  curriculum: [
    {
      title: 'Welcome',
      videos: [
        {
          title: `Getting Started/What You'll Learn`,
          duration: '7:34',
        },
        {
          title: `Split Screen Viewing, Screen Protectors + Pencil Grips`,
          duration: '3:26',
        },
        {
          title: `Downloading and Installing Class Freebies`,
          duration: '4:14',
        },
        {
          title: `Creating a New Canvas + Resolution and Color Profiles Overview`,
          duration: '8:39',
        },
      ],
    },
    {
      title: 'Interface Basics',
      videos: [
        {
          title: `Brush Basics`,
          duration: '3:06',
        },
        {
          title: `Quick Shapes`,
          duration: '1:55',
        },
        {
          title: `Color Builds + Palettes`,
          duration: '4:02',
        },
        {
          title: `Different Ways to Recolor Artwork`,
          duration: '7:27',
        },
        {
          title: `Guides + Symmetry`,
          duration: '6:05',
        },
        {
          title: `Layers + Blend Modes`,
          duration: '6:15',
        },
        {
          title: `Making, Moving + Editing Selections`,
          duration: '4:33',
        },
        {
          title: `Using + Editing Typeable Text`,
          duration: '5:27',
        },
        {
          title: `Installing Custom Fonts`,
          duration: '4:02',
        },
        {
          title: `Magic Wand, Selection Tool + Cursor`,
          duration: '8:52',
        },
        {
          title: `Preferences Overview`,
          duration: '5:43',
        },
      ],
    },
    {
      title: 'Project: Floral Initial',
      videos: [
        {
          title: `Setting Text + Background Color`,
          duration: '3:47',
        },
        {
          title: `Adding Details + Organizing Layers`,
          duration: '11:01',
        },
        {
          title: `Adding More Elements + Organizing More Layers`,
          duration: '3:42',
        },
        {
          title: `Final Details/Finishing the Project`,
          duration: '4:31',
        },
      ],
    },
    {
      title: 'Masking in Procreate',
      videos: [
        {
          title: `Clipping Masks`,
          duration: '7:47',
        },
        {
          title: `Layer Masks`,
          duration: '6:15',
        },
      ],
    },
    {
      title: 'Project: Hello',
      videos: [
        {
          title: `Setting Background Color + Text`,
          duration: '3:44',
        },
        {
          title: `Adding Flourishes + Teardrops`,
          duration: '3:11',
        },
        {
          title: `Layer Masking/Intertwining Flourishes`,
          duration: '5:23',
        },
        {
          title: `Text Highlights Using a Clipping Mask`,
          duration: '2:27',
        },
      ],
    },
    {
      title: 'Brush Settings Overview',
      videos: [
        {
          title: `Basic Settings You'll Want to Know`,
          duration: '8:13',
        },
        {
          title: `Beyond the Basic Settings`,
          duration: '9:16',
        },
      ],
    },
    {
      title: 'Project: Hey',
      videos: [
        {
          title: `Creating 3D Lettering`,
          duration: '9:30',
        },
        {
          title: `Applying Depth with Texture`,
          duration: '5:07',
        },
        {
          title: `Background + Final Details`,
          duration: '2:57',
        },
      ],
    },
    {
      title: 'Animation Basics',
      videos: [
        {
          title: `Animation Assist Palette Options`,
          duration: '7:01',
        },
        {
          title: `Simple Beating Heart Animation`,
          duration: '6:57',
        },
        {
          title: `Exporting Animations`,
          duration: '2:36',
        },
      ],
    },
    {
      title: 'Final Project: Draw',
      videos: [
        {
          title: `Project Overview`,
          duration: '2:44',
        },
        {
          title: `Creating the Artwork`,
          duration: '6:29',
        },
        {
          title: `Animating 'Drawn In' Lettering`,
          duration: '12:16',
        },
        {
          title: `Exporting the Animation`,
          duration: '2:01',
        },
      ],
    },
    {
      title: 'Export Options + Next Steps',
      videos: [
        {
          title: `Thank You + Next Steps`,
          duration: '2:38',
        },
      ],
    },
  ],
  bonuses: {
    headerImgName: `procreate101/bonuses-title.png`,
    imgAlt: 'Intro to Procreate for Beginner Bonuses',
    gridType: 2,
    items: [
      {
        title: 'Every Tuesday Brush Starter Pack',
        desc: 'We use 4 different brushes throughout the course and they’re all provided in a brush set for free so you can follow along exactly.',
        imgName: 'procreate101/bonuses-1.jpg',
        imgAlt: 'Every Tuesday Brush Starter Pack',
      },
      {
        title: 'Ink Texture Transparent PNG',
        desc: 'Play around with clipping masks in a brand new way by integrating texture! This transparent png can be applied to any background, making it truly multipurpose.',
        imgName: 'procreate101/bonuses-2.jpg',
        imgAlt: 'Ink Texture Transparent PNG',
      },
      {
        title: 'All Project Color Palettes',
        desc: 'Use the exact colors for each project in the class with these swatch downloads. 4 different color palettes you can use for any project in the future, too!',
        imgName: 'procreate101/bonuses-6.jpg',
        imgAlt: 'All Project Color Palettes',
      },
      {
        title: 'Original Procreate Project Files',
        desc: 'Access every Procreate project file in the class (the exact files made in the videos) to help if you get stuck along the way.',
        imgName: 'procreate101/bonuses-3.jpg',
        imgAlt: 'Original Procreate Project Files',
      },
    ],
  },
  courseCard: {
    title: 'Procreate 5x for Beginners',
    desc: 'New to Procreate? Begin building a solid foundation with non-destructive editing methods, an efficient workflow and prep your files beforehand for production-ready outcomes. In this beginner-friendly course, we follow simple tools tutorials and follow them up with full project tutorials using those tools. You’ll build workflow memory and confidence with your new skills as you use them in real scenarios after seeing how they behave. Utilize the free assets from the class to take your projects even further, both in the class and in the future. At the end of the course, you’ll have a firm knowledge of the program, its tools, how to create future artwork with ideas where to go next with your new skills.',
    backgroundImgName: `procreate101/sell-card-bg.jpg`,
    backgroundColor: '#73b8ad',
    cardImgName: `procreate101/course-card-title.png`,
    bullets: [
      'Access to 38 HD step-by-step videos',
      'Ink texture transparent PNG',
      'Every Tuesday brush starter pack (4 free brushes)',
      'Every project’s color palette as a downloadable swatch',
      'Lifetime access to the class',
      'Procreate animation basics',
      'Every project’s fully layered, original Procreate file',
      '4 full Procreate project tutorials',

    ],
    buttonText: 'Enroll for Free!',
    brush: {
      enabled: false,
      image: null,
    },
    priceColor: '#00d47f',
    saleColor: '#00d47f',
    stats: {
      modules: '#ef6981',
      videos: '#f3c100',
      hours: '#01b7b8',
    },

  },
  faqs: [
    {
      question: 'Is this really a free course? What’s the catch?',
      answer: 'No catch, promise! There are donation options available below (which help with course hosting fees and caffeine to keep us going), but no payment is required. If you enjoy the class, I hope you’ll check out some of my other tutorials and classes in the future!',
    },
    {
      question: 'How long can I access the course for?',
      answer: 'Forever! As long as Every-Tuesday exists, this course will be available to watch. Take it at whatever pace you’d like, watch whenever, wherever and as many times as you’d like.',
    },
    {
      question: 'Is this a live or pre-recorded course?',
      answer: 'As with all of my courses, this course is entirely pre-recorded, so you can take it whenever it works best with your schedule.',
    },
    {
      question: 'Will I be able to access all the videos and bonuses at once?',
      answer: 'Yep!',
    },
    {
      question: 'What will I need to take this class?',
      answer: 'You’ll need any iPad that provides pressure sensitivity (any of the ones located here will work: every-tuesday.com/ipads) and a compatible stylus that allows that pressure sensitivity (I recommend the Apple Pencil). In the videos, I’m using a third gen iPad Pro (12.9”, 256GB) with a second gen Apple Pencil. This course was recorded using the Procreate app version 5.1.4, so you’ll want to be on at least that to follow along.',
    },
    {
      question: 'Do I need any special Procreate brushes to create these projects?',
      answer: 'Nope! I *do* recommend downloading the free brush starter pack (included with enrollment) so you can follow along exactly with each project, though!',
    },
    {
      question: 'I was enrolled in the previous version of this course - are they the same?',
      answer: 'Yes and no. The entire course has been re-recorded and all of the projects are new, but a couple are still similar. This version is fully updated for Procreate 5X, so there are new interface videos and the section on animation is entirely new. You’ll still keep access to the previous version, and this will show up as a new course in your dashboard when you enroll.',
    },
  ],
  testimonials: [],
  letsDoThis: {
    imgName: 'procreate101/lets-do-this.png',
    altTag: 'Enroll in Procreate for beginners Today!',
    text: 'Welcome to Procreate for Beginners, the ultimate beginner\'s guide for creating beautiful Procreate artwork.',
  },
  sellCard: {
    priceColor: '#fff',
    cardImg: 'procreate101/sell-card.jpg',
    saleLink: 'https://courses.every-tuesday.com/purchase?product_id=1149386',
    paymentPlanLink: '',
    link: 'https://courses.every-tuesday.com/purchase?product_id=2693917',
    customBtnBackgroundColor: colors.primary.pink,
    h2Color: `#fff`,
    hoverColor: {
      h2: `#fff`,
      text: '#147590',
      overlay: '#01ADB3',
    },
    bullets: [
      'Instant access to all videos and bonuses',
      'All original Procreate project files',
      'Downloadable color swatches for each project',
      '2 free Procreate brushes + textures',
      'Every-Tuesday lettering guides',
    ],
    buttonText: 'Enroll for Free!',
  },
}
export default course
